import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import ServiceStatusMonitor from '../components/ServiceStatusMonitor';
import { optimizedApiService } from '../lib/apiOptimized';
import { errorHandler } from '../lib/errorHandler';
import { Search, Package, MapPin, Clock, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface TrackingForm {
  tracking_number: string;
}

const Tracking: React.FC = () => {
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [trackingData, setTrackingData] = useState<any>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TrackingForm>();

  const trackMutation = useMutation({
    mutationFn: (trackingNumber: string) => optimizedApiService.tracking.trackShipment(trackingNumber),
    onSuccess: (data: any) => {
      setTrackingData(data);
      toast.success('Tracking information retrieved successfully!');
    },
    onError: (error: any) => {
      errorHandler.handleApiError(error, 'Tracking');
      toast.error(error.response?.data?.error || 'Failed to track shipment');
      setTrackingData(null);
    },
  });

  const onSubmit = async (data: TrackingForm) => {
    setTrackingNumber(data.tracking_number);
    trackMutation.mutate(data.tracking_number);
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-success-600" />;
      case 'in_transit':
        return <Package className="h-5 w-5 text-primary-600" />;
      case 'out_for_delivery':
        return <MapPin className="h-5 w-5 text-warning-600" />;
      default:
        return <Clock className="h-5 w-5 text-secondary-600" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'delivered': return 'success';
      case 'in_transit': return 'secondary';
      case 'out_for_delivery': return 'warning';
      case 'exception': return 'error';
      default: return 'default';
    }
  };

  return (
    <div className="space-y-8">
      {/* Service Status Monitor */}
      <ServiceStatusMonitor />

      <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Track Shipment</h1>
        <p className="text-secondary-600">
          Enter a tracking number to get real-time shipment status and location updates.
        </p>
      </div>

      {/* Tracking Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>Track Package</span>
          </CardTitle>
          <CardDescription>
            Enter your UPS tracking number to get shipment details
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="flex space-x-4">
              <div className="flex-1">
                <Input
                  label="Tracking Number"
                  {...register('tracking_number', { 
                    required: 'Tracking number is required',
                    minLength: { value: 10, message: 'Tracking number must be at least 10 characters' }
                  })}
                  error={errors.tracking_number?.message}
                  placeholder="1Z999AA1234567890"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  variant="primary"
                  loading={trackMutation.isPending}
                  className="flex items-center space-x-2"
                >
                  <Search className="h-4 w-4" />
                  <span>Track</span>
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Tracking Results */}
      {trackingData && (
        <div className="space-y-6">
          {/* Shipment Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>Shipment Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm font-medium text-secondary-600">Tracking Number</p>
                  <p className="text-lg font-semibold text-secondary-900">
                    {trackingData.tracking_number}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary-600">Status</p>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(trackingData.status)}
                    <Badge variant={getStatusBadgeVariant(trackingData.status)}>
                      {trackingData.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-secondary-600">Service</p>
                  <p className="text-lg font-semibold text-secondary-900">
                    {trackingData.service_type || 'Ground'}
                  </p>
                </div>
              </div>

              {trackingData.delivery_date && (
                <div className={`mt-6 p-4 rounded-lg ${
                  trackingData.status?.toLowerCase() === 'delivered' 
                    ? 'bg-success-50 border border-success-200' 
                    : 'bg-blue-50 border border-blue-200'
                }`}>
                  <div className="flex items-center space-x-2">
                    {trackingData.status?.toLowerCase() === 'delivered' ? (
                      <CheckCircle className="h-5 w-5 text-success-600" />
                    ) : (
                      <Clock className="h-5 w-5 text-blue-600" />
                    )}
                    <p className={`font-medium ${
                      trackingData.status?.toLowerCase() === 'delivered' 
                        ? 'text-success-900' 
                        : 'text-blue-900'
                    }`}>
                      {trackingData.status?.toLowerCase() === 'delivered' 
                        ? 'Delivered on' 
                        : 'Expected Delivery:'} {new Date(trackingData.delivery_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5" />
                  <span>From Address</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trackingData.from_address ? (
                  <div className="space-y-1">
                    <p className="font-medium">{trackingData.from_address.name}</p>
                    {trackingData.from_address.company && (
                      <p className="text-sm text-secondary-600">{trackingData.from_address.company}</p>
                    )}
                    <p className="text-sm">{trackingData.from_address.address_line_1}</p>
                    {trackingData.from_address.address_line_2 && (
                      <p className="text-sm">{trackingData.from_address.address_line_2}</p>
                    )}
                    <p className="text-sm">
                      {trackingData.from_address.city}, {trackingData.from_address.state} {trackingData.from_address.postal_code}
                    </p>
                    <p className="text-sm">{trackingData.from_address.country_code}</p>
                  </div>
                ) : (
                  <p className="text-secondary-600">Address information not available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5" />
                  <span>To Address</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {trackingData.to_address ? (
                  <div className="space-y-1">
                    <p className="font-medium">{trackingData.to_address.name}</p>
                    {trackingData.to_address.company && (
                      <p className="text-sm text-secondary-600">{trackingData.to_address.company}</p>
                    )}
                    <p className="text-sm">{trackingData.to_address.address_line_1}</p>
                    {trackingData.to_address.address_line_2 && (
                      <p className="text-sm">{trackingData.to_address.address_line_2}</p>
                    )}
                    <p className="text-sm">
                      {trackingData.to_address.city}, {trackingData.to_address.state} {trackingData.to_address.postal_code}
                    </p>
                    <p className="text-sm">{trackingData.to_address.country_code}</p>
                  </div>
                ) : (
                  <p className="text-secondary-600">Address information not available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tracking Events */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5" />
                <span>Tracking Events</span>
              </CardTitle>
              <CardDescription>
                Timeline of your package's journey
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trackingData.events && trackingData.events.length > 0 ? (
                <div className="space-y-4">
                  {trackingData.events.map((event: any, index: number) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-3 h-3 bg-primary-600 rounded-full mt-2"></div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-secondary-900">{event.description}</p>
                          <p className="text-sm text-secondary-600">
                            {new Date(event.timestamp).toLocaleString()}
                          </p>
                        </div>
                        {event.location && (
                          <p className="text-sm text-secondary-600 mt-1">{event.location}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
                  <p className="text-secondary-600">No tracking events available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Results */}
      {trackingNumber && !trackingData && !trackMutation.isPending && (
        <Card>
          <CardContent className="text-center py-8">
            <Search className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <p className="text-secondary-600">No tracking information found</p>
            <p className="text-sm text-secondary-500 mt-1">
              Please check your tracking number and try again
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Tracking;
